import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Input } from './SubscriptionAppInput/Input';
import { Button } from './SubscriptionAppButton';
import { LayoutGrid, Package, Check, Info, Image as ImageIcon, ChevronDown, ArrowRight } from 'lucide-react';
interface ClubBuilderBasicsPanelProps {
  onNext?: () => void;
  boxType?: 'custom' | 'curated';
  onBoxTypeChange?: (type: 'custom' | 'curated') => void;
}
export function ClubBuilderBasicsPanel({
  onNext,
  boxType: controlledBoxType,
  onBoxTypeChange
}: ClubBuilderBasicsPanelProps) {
  const [internalBoxType, setInternalBoxType] = useState<'custom' | 'curated'>('custom');
  const boxType = controlledBoxType ?? internalBoxType;
  const setBoxType = (type: 'custom' | 'curated') => {
    onBoxTypeChange?.(type);
    setInternalBoxType(type);
  };
  const [boxName, setBoxName] = useState('');
  const [validationError, setValidationError] = useState('');
  const handleNext = () => {
    if (!boxName.trim()) {
      setValidationError('Box Name is required');
      return;
    }
    setValidationError('');
    onNext?.();
  };
  return <div className="flex flex-col gap-6" data-id="element-718">
      {/* Card 1: Club Details */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-719">
        <div className="mb-5" data-id="element-720">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-721">
            Club Details
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-722">
            The name and description shown to customers during the signup flow
          </p>
        </div>

        <div className="space-y-6" data-id="element-723">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-724">
            <div data-id="element-725">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-726">
                Box Name <span className="text-[#EF4444]" data-id="element-727">*</span>
              </label>
              <input type="text" placeholder="e.g. Estate Wine Club" value={boxName} onChange={e => {
              setBoxName(e.target.value);
              if (validationError) setValidationError('');
            }} className={`w-full h-[40px] px-3 bg-white border rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 ${validationError ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-728" />

              {validationError && <p className="mt-1.5 text-[12px] text-[#EF4444]" data-id="element-729">
                  {validationError}
                </p>}
            </div>
          </div>

          <div data-id="element-730">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-731">
              Description
            </label>
            <textarea className="w-full h-[100px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" placeholder="Add an internal description for your team..." defaultValue="" data-id="element-732" />
          </div>

          <div data-id="element-733">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-734">
              URL Slug
            </label>
            <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-735">
              <div className="bg-[#F8FAFC] px-3 py-2 text-[14px] text-[#64748B] border-r border-[#CBD5E1] flex items-center" data-id="element-736">
                yourstore.com/apps/winepik/
              </div>
              <input type="text" className="flex-1 px-3 py-2 text-[14px] text-[#0F172A] focus:outline-none" placeholder="my-wine-club" defaultValue="" data-id="element-737" />
            </div>
            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-738">
              Auto-generated from the name. Used in the signup page URL.
            </p>
          </div>
        </div>
      </Card>

      {/* Card 2: Box Type */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-739">
        <div className="mb-5" data-id="element-740">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-741">Box Type</h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-742">
            Choose how wines are selected for this club
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-743">
          {/* Custom Box Option */}
          <div onClick={() => setBoxType('custom')} className={`
              relative p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
              ${boxType === 'custom' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}
            `} data-id="element-744">
            <div className="flex justify-between items-start mb-3" data-id="element-745">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${boxType === 'custom' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#F1F5F9] text-[#64748B]'}
              `} data-id="element-746">
                <LayoutGrid className="w-6 h-6" data-id="element-747" />
              </div>
              <div className={`
                w-5 h-5 rounded-full flex items-center justify-center border
                ${boxType === 'custom' ? 'bg-[#0EA5E9] border-[#0EA5E9] text-white' : 'border-[#CBD5E1] bg-white'}
              `} data-id="element-748">
                {boxType === 'custom' && <Check className="w-3 h-3" data-id="element-749" />}
              </div>
            </div>
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-750">
              Custom Box
            </h3>
            <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-751">
              Customer chooses their own wines from a product collection. They
              pick what goes in the box.
            </p>
          </div>

          {/* Curated Box Option */}
          <div onClick={() => setBoxType('curated')} className={`
              relative p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
              ${boxType === 'curated' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}
            `} data-id="element-752">
            <div className="flex justify-between items-start mb-3" data-id="element-753">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${boxType === 'curated' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#F1F5F9] text-[#64748B]'}
              `} data-id="element-754">
                <Package className="w-6 h-6" data-id="element-755" />
              </div>
              <div className={`
                w-5 h-5 rounded-full flex items-center justify-center border
                ${boxType === 'curated' ? 'bg-[#0EA5E9] border-[#0EA5E9] text-white' : 'border-[#CBD5E1] bg-white'}
              `} data-id="element-756">
                {boxType === 'curated' && <Check className="w-3 h-3" data-id="element-757" />}
              </div>
            </div>
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-758">
              Curated Box
            </h3>
            <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-759">
              You select the wines — the customer receives a pre-built pack.
              Great for tasting experiences.
            </p>
          </div>
        </div>

        <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-760">
          <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" data-id="element-761" />
          <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-762">
            <span className="font-semibold text-[#0F172A]" data-id="element-763">Custom boxes</span>{' '}
            show a product browser where customers pick wines.{' '}
            <span className="font-semibold text-[#0F172A]" data-id="element-764">Curated boxes</span>{' '}
            display pre-selected wines as read-only. Selling plans and pricing
            work the same for both types.
          </p>
        </div>
      </Card>

      {/* Card 3: Box Image */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-765">
        <div className="mb-5" data-id="element-766">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-767">
            Box Image
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-768">
            Featured image shown on the signup page and customer portal
          </p>
        </div>

        <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-12 flex flex-col items-center justify-center text-center hover:bg-[#F8FAFC] transition-colors cursor-pointer" data-id="element-769">
          <div className="w-12 h-12 bg-[#F1F5F9] rounded-lg flex items-center justify-center mb-4 text-[#94A3B8]" data-id="element-770">
            <ImageIcon className="w-6 h-6" data-id="element-771" />
          </div>
          <p className="text-[14px] font-medium text-[#64748B] mb-1" data-id="element-772">
            Click to upload or drag and drop
          </p>
          <p className="text-[12px] text-[#94A3B8]" data-id="element-773">
            PNG, JPG, WebP up to 5MB · Recommended 1200 x 630px
          </p>
        </div>
      </Card>

      {/* Card 4: Storefront Settings */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-774">
        <div className="mb-5" data-id="element-775">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-776">
            Storefront Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-777">
            Additional options for how this box appears on the storefront
          </p>
        </div>

        <div className="space-y-6" data-id="element-778">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-779">
            <div data-id="element-780">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-781">
                Shipping Display
              </label>
              <div className="h-[40px] px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] flex items-center gap-2" data-id="element-782">
                <svg className="w-4 h-4 text-[#10B981] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-783">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" data-id="element-784" />
                </svg>
                <span className="text-[14px] text-[#475569]" data-id="element-785">
                  Syncs from Shopify
                </span>
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-786">
                Shipping rates are managed in your Shopify shipping settings and
                applied automatically at checkout.
              </p>
            </div>

            <div data-id="element-787">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-788">
                Availability
              </label>
              <div className="relative" data-id="element-789">
                <select className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none" defaultValue="all" data-id="element-790">
                  <option value="all" data-id="element-791">Available to all customers</option>
                  <option value="tagged" data-id="element-792">Only customers with tag...</option>
                  <option value="link" data-id="element-793">Only via direct link</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748B] w-4 h-4 pointer-events-none" data-id="element-794" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-795">
                Restrict who can see and subscribe to this box
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Next Button */}
      <div className="flex justify-end pt-2" data-id="element-796">
        <Button variant="primary" onClick={handleNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-798" />} data-id="element-797">
          Next: Pack Sizes
        </Button>
      </div>
    </div>;
}